import { useCallback, useEffect, type Dispatch, type SetStateAction } from 'react';
import type { DayPunchRecord, Employee, EmployeeSession, PunchEvent } from '../../types/employee';
import {
  getEmployeeEventsForDate,
  getTodayPunchStatus,
  getWeekPunchRecords,
  todayDateKey,
} from '../../services/punchService';
import { loadPunchEvents, subscribeToPunchStorage } from '../../services/punchStorage';
import {
  getCurrentWeekKey,
  getEmployeeWeekSchedule,
  hasAssignedShifts,
  summarizeWeekSchedule,
  type DayScheduleSummary,
} from '../../services/employeeScheduleService';
import type { EmployeeWeeklySchedule } from '../../modules/shift-management/types';
import { employeeIdAliases } from '../../lib/employeeIds';

export function useWorkforceScheduleQueries(
  session: EmployeeSession | null,
  punchEvents: PunchEvent[],
  setPunchEvents: Dispatch<SetStateAction<PunchEvent[]>>,
  scheduleTick: number,
  setScheduleTick: Dispatch<SetStateAction<number>>,
  setPunchRevision: Dispatch<SetStateAction<number>>,
  employees: Employee[] = []
) {
  const resolveEmployeeId = useCallback(
    (employeeId?: string) => employeeId ?? session?.employeeId ?? null,
    [session]
  );

  /**
   * Expands an id to [convexId, legacyId] so punches and schedules written
   * before the Convex migration (keyed by `teacher-N`) still resolve.
   */
  const resolveIdKey = useCallback(
    (employeeId?: string): string[] | null => {
      const id = resolveEmployeeId(employeeId);
      if (!id) return null;
      const employee = employees.find((e) => e.id === id);
      return employee ? employeeIdAliases(employee) : [id];
    },
    [resolveEmployeeId, employees]
  );

  const getTodayStatus = useCallback(
    (employeeId?: string) => {
      const idKey = resolveIdKey(employeeId);
      if (!idKey) {
        return {
          clockIn: null,
          clockOut: null,
          lunchStart: null,
          lunchEnd: null,
          workState: 'none' as const,
          isCurrentlyIn: false,
        };
      }
      return getTodayPunchStatus(punchEvents, idKey);
    },
    [punchEvents, resolveIdKey]
  );

  const getTodayEvents = useCallback(
    (employeeId?: string) => {
      const idKey = resolveIdKey(employeeId);
      if (!idKey) return [];
      return getEmployeeEventsForDate(punchEvents, idKey, todayDateKey());
    },
    [punchEvents, resolveIdKey]
  );

  const getWeekPunchRecordsForEmployee = useCallback(
    (employeeId?: string): DayPunchRecord[] => {
      const idKey = resolveIdKey(employeeId);
      if (!idKey) return [];
      return getWeekPunchRecords(punchEvents, idKey);
    },
    [punchEvents, resolveIdKey]
  );

  const getWeekSchedule = useCallback(
    (employeeId?: string, weekKey?: string): EmployeeWeeklySchedule | null => {
      const idKey = resolveIdKey(employeeId);
      if (!idKey) return null;
      return getEmployeeWeekSchedule(idKey, weekKey ?? getCurrentWeekKey());
    },
    [resolveIdKey, scheduleTick]
  );

  const getWeekSummary = useCallback(
    (employeeId?: string, weekKey?: string): DayScheduleSummary[] => {
      const schedule = getWeekSchedule(employeeId, weekKey);
      return summarizeWeekSchedule(schedule);
    },
    [getWeekSchedule]
  );

  const hasShiftsAssigned = useCallback(
    (employeeId?: string, weekKey?: string) => {
      const schedule = getWeekSchedule(employeeId, weekKey);
      return hasAssignedShifts(schedule);
    },
    [getWeekSchedule]
  );

  const refreshScheduleData = useCallback(() => {
    setScheduleTick((n) => n + 1);
  }, [setScheduleTick]);

  const refreshPunchData = useCallback(() => {
    const loaded = loadPunchEvents();
    setPunchEvents((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(loaded)) return prev;
      setPunchRevision((n) => n + 1);
      return loaded;
    });
  }, [setPunchEvents, setPunchRevision]);

  useEffect(() => {
    const onPublished = () => refreshScheduleData();
    window.addEventListener('garderie:shifts-published', onPublished);
    return () => window.removeEventListener('garderie:shifts-published', onPublished);
  }, [refreshScheduleData]);

  useEffect(() => subscribeToPunchStorage(refreshPunchData), [refreshPunchData]);

  return {
    getTodayStatus,
    getTodayEvents,
    getWeekPunchRecords: getWeekPunchRecordsForEmployee,
    getWeekSchedule,
    getWeekSummary,
    hasShiftsAssigned,
    refreshScheduleData,
    refreshPunchData,
  };
}
